import {
  BASE_PATH,
  CATEGORIES,
  DEFAULT_DURATION,
  DURATIONS,
  isDuration,
  isValidRoomCode,
  MIXED,
  NAME_MAX_LENGTH,
  ROOM_CODE_LENGTH,
  sanitizeName,
  type ApiErrorCode,
  type CategoryChoice,
  type ClientConfig,
  type CreateRoomRequest,
  type Duration,
  type VerifyRoomRequest,
} from "../src/protocol";
import { LANGS, type Lang } from "../src/languages";
import { track } from "./analytics";
import { h } from "./dom";
import { LANG } from "./i18n";
import { uiLangSelect } from "./langPicker";
import { getDurationPreference, getName, setDurationPreference, setName } from "./storage";
import { LANGUAGE_NAMES, STRINGS } from "./strings";
import { TurnstileGate } from "./turnstile";

/** The WorkBrawl hub this title belongs to, in the visitor's language. */
const HUB_HOME = LANG === "es" ? "/es/" : "/";

/** Home screen: create a room or join one by code. `notice` explains a redirect here. */
export function renderHome(root: HTMLElement, notice?: string): void {
  const nameInput = h("input", {
    id: "name",
    name: "name",
    type: "text",
    autocomplete: "nickname",
    maxlength: NAME_MAX_LENGTH,
    placeholder: STRINGS.namePlaceholder,
    required: true,
  });
  nameInput.value = getName();

  const savedDuration = getDurationPreference();
  const durationInput = h(
    "select",
    { id: "duration", name: "duration", class: "select" },
    ...DURATIONS.map((seconds) => h("option", { value: seconds }, STRINGS.minutes(seconds / 60))),
  );
  durationInput.value = String(isDuration(savedDuration) ? savedDuration : DEFAULT_DURATION);

  // Puzzles default to the language of whoever opens the room, but can be changed.
  const langInput = h(
    "select",
    { id: "puzzle-lang", name: "puzzle-lang", class: "select" },
    ...LANGS.map((lang) => h("option", { value: lang }, LANGUAGE_NAMES[lang])),
  );
  langInput.value = LANG;

  const categoryInput = h(
    "select",
    { id: "category", name: "category", class: "select" },
    ...[MIXED, ...CATEGORIES].map((id) => h("option", { value: id }, STRINGS.categories[id as CategoryChoice])),
  );

  const codeInput = h("input", {
    id: "code",
    name: "code",
    type: "text",
    autocomplete: "off",
    autocapitalize: "characters",
    spellcheck: "false",
    maxlength: ROOM_CODE_LENGTH,
    placeholder: STRINGS.codePlaceholder,
    class: "code-input",
  });
  codeInput.addEventListener("input", () => {
    codeInput.value = codeInput.value.toUpperCase().replace(/\s+/g, "");
  });

  const error = h("p", { class: "form-error", role: "alert" });
  /** Join problems (bad code, missing room) show right under the code field. */
  const codeError = h("p", { class: "form-error", role: "alert" });
  codeInput.addEventListener("input", () => (codeError.textContent = ""));
  const createButton = h("button", { type: "button", class: "btn btn-primary" }, STRINGS.createRoom);
  const joinButton = h("button", { type: "submit", class: "btn" }, STRINGS.join);
  const turnstileSlot = h("div", { class: "turnstile-slot" });
  const turnstileHint = h("p", { class: "turnstile-hint", role: "status", hidden: true }, STRINGS.turnstileInteract);
  const gate = setupTurnstile(turnstileSlot, error, (needed) => (turnstileHint.hidden = !needed));
  /** A spent or failed check is reset, so another click can work; a missing widget can't. */
  const checkFailedText = async () =>
    (await gate.then(
      () => true,
      () => false,
    ))
      ? STRINGS.turnstileFailed
      : STRINGS.turnstileUnavailable;

  const readName = (): string | null => {
    const name = sanitizeName(nameInput.value);
    if (name === null) {
      error.textContent = STRINGS.nameInvalid;
      nameInput.focus();
      return null;
    }
    setName(name);
    return name;
  };

  /** Disables `button` while `task` runs (with a Turnstile token) and always resets the widget. */
  const protectedAction = async (
    button: HTMLButtonElement,
    busyLabel: string,
    task: (token: string) => Promise<void>,
  ): Promise<void> => {
    const label = button.textContent;
    button.disabled = true;
    button.textContent = STRINGS.verifying;
    try {
      const token = await (await gate).take();
      button.textContent = busyLabel;
      await task(token);
    } finally {
      button.disabled = false;
      button.textContent = label;
      (await gate.catch(() => null))?.reset();
    }
  };

  createButton.addEventListener("click", async () => {
    error.textContent = "";
    codeError.textContent = "";
    if (!readName()) return;
    const duration = Number(durationInput.value) as Duration;
    setDurationPreference(duration);
    await protectedAction(createButton, STRINGS.creatingRoom, async (turnstileToken) => {
      try {
        const body: CreateRoomRequest = {
          duration,
          lang: langInput.value as Lang,
          category: categoryInput.value as CategoryChoice,
          turnstileToken,
        };
        const res = await postJson(`${BASE_PATH}/api/rooms`, body);
        if (res.status === 403) {
          error.textContent = STRINGS.turnstileFailed;
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { code } = (await res.json()) as { code: string };
        track("room_created", { duration });
        location.assign(`${BASE_PATH}/r/${code}`);
      } catch (err) {
        console.error("[StickBrawl] create room failed", err);
        error.textContent = STRINGS.createFailed;
      }
    }).catch(async () => (error.textContent = await checkFailedText()));
  });

  const joinForm = h(
    "form",
    { class: "join-form", novalidate: true },
    h("label", { for: "code" }, STRINGS.codeLabel),
    h("div", { class: "join-row" }, codeInput, joinButton),
    codeError,
  );
  joinForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    error.textContent = "";
    codeError.textContent = "";
    if (!readName()) return;
    const code = codeInput.value.trim().toUpperCase();
    if (!isValidRoomCode(code)) {
      codeError.textContent = STRINGS.codeInvalid;
      codeInput.focus();
      return;
    }
    await protectedAction(joinButton, STRINGS.verifying, async (turnstileToken) => {
      try {
        const body: VerifyRoomRequest = { turnstileToken };
        const res = await postJson(`${BASE_PATH}/api/rooms/${code}/verify`, body);
        if (res.ok) {
          track("room_joined_by_code");
          location.assign(`${BASE_PATH}/r/${code}`);
          return;
        }
        const { error: apiError } = (await res.json().catch(() => ({}))) as { error?: ApiErrorCode };
        codeError.textContent =
          apiError === "ROOM_NOT_FOUND"
            ? STRINGS.errorRoomNotFound
            : apiError === "TURNSTILE_FAILED"
              ? STRINGS.turnstileFailed
              : STRINGS.joinFailed;
        codeInput.focus();
      } catch (err) {
        console.error("[StickBrawl] room check failed", err);
        codeError.textContent = STRINGS.joinFailed;
      }
    }).catch(async () => (codeError.textContent = await checkFailedText()));
  });

  root.replaceChildren(
    h(
      "main",
      { class: "home" },
      h(
        "div",
        { class: "home-top" },
        h("a", { href: HUB_HOME, class: "hub-link", "aria-label": STRINGS.backToHub, title: STRINGS.backToHub }, "← WorkBrawl"),
        uiLangSelect(),
      ),
      h(
        "header",
        { class: "home-hero" },
        h("h1", { class: "brand brand-large" }, brandMark()),
        h("p", { class: "tagline" }, STRINGS.tagline),
        h("p", { class: "intro" }, STRINGS.homeIntro),
      ),
      h(
        "section",
        { class: "card home-card brawl-panel" },
        h("h2", { class: "panel-ribbon" }, STRINGS.newRoomTitle),
        notice ? h("p", { class: "notice", role: "status" }, notice) : null,
        h("label", { for: "name" }, STRINGS.nameLabel),
        nameInput,
        h("label", { for: "duration", class: "field-gap" }, STRINGS.durationLabel),
        durationInput,
        h("label", { for: "puzzle-lang", class: "field-gap" }, STRINGS.puzzleLangLabel),
        langInput,
        h("label", { for: "category", class: "field-gap" }, STRINGS.categoryLabel),
        categoryInput,
        createButton,
        h("div", { class: "divider" }, h("span", {}, STRINGS.or)),
        joinForm,
        error,
        turnstileHint,
        turnstileSlot,
      ),
      h(
        "section",
        { class: "info" },
        h("h2", {}, STRINGS.howTitle),
        h(
          "ol",
          { class: "steps" },
          ...STRINGS.howSteps.map((step) => h("li", {}, h("strong", {}, step.title), " ", step.text)),
        ),
        h("h2", {}, STRINGS.faqTitle),
        h("dl", { class: "faq" }, ...STRINGS.faq.flatMap((item) => [h("dt", {}, item.q), h("dd", {}, item.a)])),
      ),
      siteFooter(),
    ),
  );
}

/** Same footer on every screen: back to the hub, plus its privacy policy and terms. */
export function siteFooter(): HTMLElement {
  // Privacy, terms and the cookie choice belong to the hub and cover every title.
  return h(
    "footer",
    { class: "site-footer" },
    h("a", { href: HUB_HOME }, "WorkBrawl"),
    " · ",
    h("a", { href: `${HUB_HOME}${LANG === "es" ? "privacidad/" : "privacy/"}` }, STRINGS.privacy),
    " · ",
    h("a", { href: `${HUB_HOME}${LANG === "es" ? "terminos/" : "terms/"}` }, STRINGS.terms),
  );
}

export function brandMark(): DocumentFragment {
  const frag = document.createDocumentFragment();
  frag.append("Stick", h("span", { class: "brand-accent" }, "Brawl"));
  return frag;
}

function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Fetches the site key and mounts the widget; the promise rejects if Turnstile is unavailable. */
function setupTurnstile(
  slot: HTMLElement,
  error: HTMLElement,
  onInteractive: (needed: boolean) => void,
): Promise<TurnstileGate> {
  const gate = (async () => {
    const res = await fetch(`${BASE_PATH}/api/config`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const { turnstileSiteKey } = (await res.json()) as ClientConfig;
    const turnstile = new TurnstileGate(slot, turnstileSiteKey, onInteractive);
    await turnstile.mount();
    return turnstile;
  })();
  gate.catch((err) => {
    console.error("[StickBrawl] Turnstile unavailable", err);
    error.textContent = STRINGS.turnstileUnavailable;
  });
  return gate;
}
